import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DocumentPicker } from './DocumentPicker.js';

interface LegalHoldRow {
  readonly holdId: string;
  readonly documentCode: string | null;
  readonly documentName: string | null;
  readonly holdReference: string;
  readonly holdReason: string;
  readonly status: string;
  readonly appliedAt: string;
}

export function M23LegalHoldDashboardPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<LegalHoldRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState('');
  const [holdReference, setHoldReference] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  function load(): void {
    setError(null);
    apiRequest<{ data: LegalHoldRow[] }>('/v1/legal-holds/active', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  function onApply(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/legal-holds/documents/${encodeURIComponent(id)}`, {
      method: 'POST',
      token,
      body: { holdReference, holdReason },
    })
      .then(() => { setMessage(t('m23.legalHold.msgApplied')); setDocumentId(''); setHoldReference(''); setHoldReason(''); load(); })
      .catch((err) => setError((err as Error).message));
  }

  function onRelease(holdId: string): void {
    const releaseReason = window.prompt(t('m23.legalHold.releasePrompt'));
    if (releaseReason === null) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/legal-holds/${encodeURIComponent(holdId)}/release`, {
      method: 'POST',
      token,
      body: { releaseReason: releaseReason.trim() },
    })
      .then(() => { setMessage(t('m23.legalHold.msgReleased')); load(); })
      .catch((err) => setError((err as Error).message));
  }

  return (
    <main aria-label="legal-hold-dashboard">
      <h1>{t('m23.legalHold.title')}</h1>
      <form aria-label="legal-hold-apply-form" onSubmit={onApply}>
        <DocumentPicker label={t('m23.legalHold.documentId')} value={documentId} onPick={(id) => setDocumentId(id)} />
        <label htmlFor="holdReference">{t('m23.legalHold.holdReference')}</label>
        <input id="holdReference" value={holdReference} onChange={(e) => setHoldReference(e.target.value)} />
        <label htmlFor="holdReason">{t('m23.legalHold.holdReason')}</label>
        <input id="holdReason" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} />
        <button type="submit">{t('m23.legalHold.applyHold')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {rows && (
        <table aria-label="legal-holds-table">
          <thead><tr><th>{t('m23.legalHold.colReference')}</th><th>{t('m23.legalHold.colDocument')}</th><th>{t('m23.legalHold.colReason')}</th><th>{t('m23.legalHold.colStatus')}</th><th>{t('m23.legalHold.colAppliedAt')}</th><th>{t('m23.legalHold.colAction')}</th></tr></thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.holdId}>
                <td>{h.holdReference}</td>
                <td>{[h.documentCode, h.documentName].filter(Boolean).join(' \u2014 ')}</td>
                <td>{h.holdReason}</td>
                <td>{h.status}</td>
                <td>{at(h.appliedAt)}</td>
                <td><button type="button" onClick={() => onRelease(h.holdId)}>{t('m23.legalHold.release')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
